/**
 * Admin Dashboard - Endpoint-Verwaltung
 */
import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Badge,
  Spinner,
  Alert,
  Modal,
  Form,
  Row,
  Col
} from 'react-bootstrap';
import {
  listEndpoints,
  createEndpoint,
  updateEndpoint,
  deleteEndpoint
} from '../../services/api';

function AdminDashboard() {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [formData, setFormData] = useState(getEmptyFormData());

  useEffect(() => {
    loadEndpoints();
  }, []);

  const loadEndpoints = async () => {
    try {
      setError(null);
      const response = await listEndpoints();
      setEndpoints(response.data);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Fehler beim Laden der Endpoints');
    } finally {
      setLoading(false);
    }
  };

  function getEmptyFormData() {
    return {
      name: '',
      url: '',
      description: '',
      sensor_type: 'http',
      expected_value: '',
      check_interval: 300,
      is_active: true,
      expected_status_code: 200,
      timeout: 30,
      group: '',
      tags: ''
    };
  }

  // Typabhängige Beschriftung des Ziel-Felds
  const SENSOR_TYPES = {
    http: { label: 'HTTP(S) Webcheck', urlLabel: 'URL', placeholder: 'https://example.com/api/health' },
    tcp: { label: 'TCP-Port', urlLabel: 'Ziel (tcp://host:port)', placeholder: 'tcp://db.example.com:5432' },
    dns: { label: 'DNS-Check', urlLabel: 'Ziel (dns://hostname)', placeholder: 'dns://example.com' },
    tls: { label: 'TLS-Zertifikat', urlLabel: 'Ziel (tls://host:port)', placeholder: 'tls://example.com:443' },
  };

  const handleShowModal = (endpoint = null) => {
    if (endpoint) {
      setEditingEndpoint(endpoint);
      setFormData({
        ...getEmptyFormData(),
        ...endpoint,
        sensor_type: endpoint.sensor_type || 'http',
        expected_value: endpoint.expected_value || '',
      });
    } else {
      setEditingEndpoint(null);
      setFormData(getEmptyFormData());
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEndpoint(null);
    setFormData(getEmptyFormData());
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      // Konvertiere numerische Werte
      const data = {
        ...formData,
        check_interval: parseInt(formData.check_interval, 10),
        expected_status_code: parseInt(formData.expected_status_code, 10),
        timeout: parseInt(formData.timeout, 10),
        expected_value: formData.expected_value || null,
      };

      if (editingEndpoint) {
        await updateEndpoint(editingEndpoint.id, data);
        setSuccess('Endpoint erfolgreich aktualisiert');
      } else {
        await createEndpoint(data);
        setSuccess('Endpoint erfolgreich erstellt');
      }

      handleCloseModal();
      loadEndpoints();
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError(err.response?.data?.detail || 'Fehler beim Speichern des Endpoints');
    }
  };

  const handleDelete = async (endpointId, endpointName) => {
    if (!window.confirm(`Endpoint "${endpointName}" wirklich löschen?`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await deleteEndpoint(endpointId);
      setSuccess('Endpoint erfolgreich gelöscht');
      loadEndpoints();
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      setError('Fehler beim Löschen des Endpoints');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="success" className="spinner-pcs" />
        <p className="mt-3">Lade Endpoints...</p>
      </div>
    );
  }

  return (
    <div className="admin-content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Endpoint-Verwaltung</h2>
        <Button variant="success" className="btn-pcs" onClick={() => handleShowModal()}>
          + Neuer Endpoint
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <Card.Body>
          {endpoints.length === 0 ? (
            <Alert variant="info">
              Noch keine Endpoints konfiguriert. Erstellen Sie einen neuen Endpoint!
            </Alert>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Typ</th>
                  <th>Ziel</th>
                  <th>Intervall</th>
                  <th>Status</th>
                  <th>Gruppe</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {endpoints.map((endpoint) => (
                  <tr key={endpoint.id}>
                    <td><strong>{endpoint.name}</strong></td>
                    <td>
                      <Badge bg={endpoint.sensor_type === 'http' || !endpoint.sensor_type ? 'secondary' : 'info'}>
                        {(endpoint.sensor_type || 'http').toUpperCase()}
                      </Badge>
                    </td>
                    <td><code>{endpoint.url}</code></td>
                    <td>{endpoint.check_interval}s</td>
                    <td>
                      <Badge bg={endpoint.is_active ? 'success' : 'secondary'}>
                        {endpoint.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </td>
                    <td>{endpoint.group || '-'}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => handleShowModal(endpoint)}
                      >
                        Bearbeiten
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(endpoint.id, endpoint.name)}
                      >
                        Löschen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal für Erstellen/Bearbeiten */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingEndpoint ? 'Endpoint bearbeiten' : 'Neuer Endpoint'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="z.B. Produktions-API"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gruppe</Form.Label>
                  <Form.Control
                    type="text"
                    name="group"
                    value={formData.group}
                    onChange={handleInputChange}
                    placeholder="z.B. Production"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Sensor-Typ *</Form.Label>
              <Form.Select
                name="sensor_type"
                value={formData.sensor_type}
                onChange={handleInputChange}
              >
                {Object.entries(SENSOR_TYPES).map(([value, cfg]) => (
                  <option key={value} value={value}>{cfg.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{SENSOR_TYPES[formData.sensor_type]?.urlLabel || 'URL'} *</Form.Label>
              <Form.Control
                type={formData.sensor_type === 'http' ? 'url' : 'text'}
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                required
                placeholder={SENSOR_TYPES[formData.sensor_type]?.placeholder}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Optionale Beschreibung"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Check-Intervall (Sekunden) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="check_interval"
                    value={formData.check_interval}
                    onChange={handleInputChange}
                    required
                    min="60"
                    max="86400"
                  />
                  <Form.Text className="text-muted">60-86400</Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                {formData.sensor_type === 'http' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Erwarteter Status-Code *</Form.Label>
                    <Form.Control
                      type="number"
                      name="expected_status_code"
                      value={formData.expected_status_code}
                      onChange={handleInputChange}
                      required
                      min="100"
                      max="599"
                    />
                  </Form.Group>
                )}
                {formData.sensor_type === 'dns' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Erwartete IP(s)</Form.Label>
                    <Form.Control
                      type="text"
                      name="expected_value"
                      value={formData.expected_value}
                      onChange={handleInputChange}
                      placeholder="z.B. 46.62.234.71"
                    />
                    <Form.Text className="text-muted">Optional, kommasepariert</Form.Text>
                  </Form.Group>
                )}
                {formData.sensor_type === 'tls' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Warnschwelle (Tage)</Form.Label>
                    <Form.Control
                      type="number"
                      name="expected_value"
                      value={formData.expected_value}
                      onChange={handleInputChange}
                      min="0"
                      max="365"
                      placeholder="14"
                    />
                    <Form.Text className="text-muted">Down, wenn Zertifikat kürzer gültig</Form.Text>
                  </Form.Group>
                )}
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Timeout (Sekunden) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="timeout"
                    value={formData.timeout}
                    onChange={handleInputChange}
                    required
                    min="1"
                    max="120"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="is_active"
                label="Monitoring aktiv"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Abbrechen
            </Button>
            <Button variant="success" type="submit" className="btn-pcs">
              {editingEndpoint ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminDashboard;
